import { SideBarDef } from "ag-grid-enterprise";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import ConditionalFormattingToolPanel from "components/conditional-formatting";
import { TCiqAgGrid } from "components/ciq-grid-view-wrapper/model";
import { GridLoadingIndicator } from "components/widgets";
import { ProjectContext } from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useState } from "react";

const EMPTY_EXTEND_TOOL_PANELS: SideBarDef["toolPanels"] = [];

function CiqAgGrid<TData = any>(props: TCiqAgGrid) {
  const {
    gridRef: ref,
    rowData,
    additionalGridComponent,
    disableResizingAutoWidth = false,
    extendToolPanels = EMPTY_EXTEND_TOOL_PANELS,
    ...agGridProps
  } = props as TCiqAgGrid & {
    extendToolPanels?: SideBarDef["toolPanels"];
  };
  const [isGridReady, setGridReady] = useState(false);
  const { isProjectArchived } = useContext(ProjectContext);

  // Archived projects are read-only; colDefs with `editable: true` can still
  // be opened from the keyboard (Enter/F2/typing), so cancel those edits here.
  const archivedEditOverrides: Partial<AgGridReactProps<TData>> =
    isProjectArchived
      ? {
          onCellEditingStarted: (event) => event.api.stopEditing(true),
          onCellEditRequest: undefined,
          onCellValueChanged: undefined
        }
      : {};

  const gridOptions: AgGridReactProps<TData> = {
    headerHeight: 36,
    defaultColDef: {
      sortable: true,
      editable: false,
      filter: false,
      resizable: true,
      flex: 1
    },
    overlayNoRowsTemplate: "No Data Found",
    autoGroupColumnDef: {
      headerName: "",
      field: "",
      cellRenderer: "agGroupCellRenderer"
    },
    loadingOverlayComponent: GridLoadingIndicator,
    suppressAggFuncInHeader: true,
    suppressDragLeaveHidesColumns: true,
    suppressContextMenu: true,
    suppressClickEdit: true,
    cacheQuickFilter: true,
    maintainColumnOrder: true,
    readOnlyEdit: true,
    enableCellTextSelection: true,
    animateRows: false,
    tooltipShowMode: "whenTruncated",
    rowSelection: {
      mode: "multiRow",
      selectAll: "filtered",
      checkboxLocation: "autoGroupColumn"
    },
    selectionColumnDef: {
      sortable: true,
      resizable: false,
      width: 40,
      suppressHeaderMenuButton: true,
      pinned: "left",
      lockPinned: true
    },
    onGridReady: () => {
      setGridReady(true);
    },
    ...agGridProps,
    ...archivedEditOverrides,
    rowData
  } as AgGridReactProps;

  useEffect(() => {
    if (ref.current && isGridReady && rowData?.length === 0) {
      const { api } = ref.current;
      if (api) {
        api.setGridOption("loading", false);
      }
    }
  }, [rowData, isGridReady, ref]);

  useEffect(() => {
    if (disableResizingAutoWidth) return;
    function handleResize() {
      if (isGridReady && ref && ref.current) {
        ref.current!.api.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [ref, isGridReady, disableResizingAutoWidth]);

  const sideBar = useMemo(
    () =>
      ({
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressSideButtons: true,
              suppressColumnSelectAll: true
            }
          },
          ...(extendToolPanels || [])
        ],
        position: "right"
      }) as SideBarDef,
    [extendToolPanels]
  );

  return (
    <div className="grow ag-theme-alpine relative">
      <AgGridReact<TData> sideBar={sideBar} {...gridOptions} ref={ref} />
      {additionalGridComponent}
      <ConditionalFormattingToolPanel gridRef={ref} />
    </div>
  );
}
export default CiqAgGrid;
